import { Col, Row } from 'antd';
import classNames from 'classnames';
import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ProjectId, TaskId } from 'api/models';
import { Breadcrumb } from 'components/atoms/breadcrumb';
import { LinkToTask } from 'components/atoms/link-to-task';
import { TaskNameInput } from 'components/molecules/tasks/task-name-input';
import { ClassNamePropsWithChildren } from 'components/ozone-component';
import { Text02 } from 'components/styles';
import { useProject } from 'data/projects/hooks';
import { Task } from 'data/tasks';
import { ProjectResource, useWithAccess } from 'hooks/rbac/useWithAccess';
import { truncate } from 'utils/truncate';
import { getProjectPlanUrl } from 'utils/urls';
import styles from './header.module.css';

interface HeaderProps extends ClassNamePropsWithChildren {
  data: Task;
  projectId: number;
  handleNameChange: (value: string) => void;
  projectResource: ProjectResource | undefined;
}

// below css reference - https://codepen.io/StijnDeWitt/pen/LryNxa

const Wrapper = styled.div`
  position: sticky;
  height: 100px;
  top: -8px;
  z-index: var(--zindex-layer-09);
  &::before,
  &::after {
    content: '';
    height: 8px;
    display: block;
    /* make pseudo elements sticky as well */
    position: sticky;
    -webkit-position: sticky;
  }
  &::before {
    top: 82px;
    box-shadow: var(--shadow-08);
  }
  &::after {
    content: '';
    background: var(--task-pane-header-bg);
    top: 0;
    z-index: 15;
  }
`;

const StyledHeader = styled.header`
  padding-bottom: 0;
  height: 92px;
  position: sticky;
  -webkit-position: sticky;
  top: 0px;
  /* compensate for shadow with negative margin */
  margin-top: -8px;
  z-index: 20;
`;

const StyledBreadcrumb = styled(Breadcrumb)`
  ${Text02};
  & > span:last-child a:hover {
    color: var(--primary-color);
  }
`;

export const HeaderTaskName: FC<HeaderProps> = ({
  className,
  data,
  projectId,
  handleNameChange,
  projectResource,
}) => {
  const project = useProject(ProjectId(projectId));
  const { hasAccessToResource } = useWithAccess();

  const canEditTaskName = hasAccessToResource({
    rule: 'TASK_NAME:WRITE',
    resourceData: projectResource,
  });

  return (
    <Wrapper>
      <StyledHeader className={`${styles.header} ${className}`}>
        <StyledBreadcrumb>
          {project ? (
            <Breadcrumb.Item>
              <Link
                data-cy="task_drawer.breadcrumb.project_name"
                to={getProjectPlanUrl(ProjectId(projectId))}
              >
                <span title={project.projectName}>
                  {truncate(project.projectName, 25, 10)}
                </span>
              </Link>
            </Breadcrumb.Item>
          ) : null}

          {/* TODO:: This is an interim fix to enable task pane from breaking */}
          {data.parentTaskObject?.project && data.parentTaskObject?.taskId && (
            <Breadcrumb.Item>
              <LinkToTask
                id={TaskId(
                  data.parentTaskObject.project.projectId,
                  data.parentTaskObject.taskId,
                )}
              >
                <span title={data.parentTaskObject?.taskName}>
                  {truncate(data.parentTaskObject?.taskName, 25, 10)}
                </span>
              </LinkToTask>
            </Breadcrumb.Item>
          )}
        </StyledBreadcrumb>
        <Row align="middle" className={styles.input__row}>
          <Col span={20}>
            <TaskNameInput
              className={styles.task_name}
              value={data.taskName}
              onPressEnter={handleNameChange}
              inputProps={{
                className: classNames(styles.task_name_input),
                onBlur: ({ currentTarget: { value } }) =>
                  data.taskName !== value && handleNameChange(value),
                disabled: !canEditTaskName,
              }}
            />
          </Col>
        </Row>
      </StyledHeader>
    </Wrapper>
  );
};
